import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@heroui/button";
import { Pagination } from "@heroui/pagination";
import { Tooltip } from "@heroui/tooltip";
import { Card, CardHeader, CardBody } from "@heroui/card";
import { Spinner } from "@heroui/spinner";
import { getPressReleases } from "@/src/app/hooks/content.hooks";
import { Divider } from "@heroui/divider";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from "@heroui/modal";

function createMarkup(htmlString) {
  return { __html: htmlString };
}

function truncateString(str, num) {
  if (str.length <= num) {
    return str;
  }
  return str.slice(0, num) + "...";
}

export default function AnalystsNotes({ companyId }) {
  const itemsPerPage = 2;
  const [analystsNotes, setAnalystsNotes] = useState([]);
  const [sort, setSort] = useState("desc");
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true); // Added isLoading state
  const [expanded, setExpanded] = useState({});
  const [selectedComment, setSelectedComment] = useState(""); // For managing modal content
  const { isOpen, onOpen, onOpenChange } = useDisclosure(); // Hook for modal visibility

  useEffect(() => {
    const fetchAnalystNotes = async () => {
      setIsLoading(true);
      try {
        const response = await getPressReleases(
          currentPage,
          companyId,
          sort,
          itemsPerPage
        );

        if (response.status === 200) {
          setAnalystsNotes(response.data.data);
          setTotalPages(response.data.pagination.last_page);
        } else {
          console.log("No data received");
          setAnalystsNotes([]);
          setTotalPages(0);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setAnalystsNotes([]);
        setTotalPages(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalystNotes();
  }, [companyId, currentPage, sort, itemsPerPage]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const openModal = (comment) => {
    setSelectedComment(comment); // Set the selected comment
    onOpen(); // Open the modal
  };

  return (
    <div>
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Spinner color="primary" />
        </div>
      ) : analystsNotes.length > 0 ? (
        analystsNotes.map((item) => (
          <div
            key={item.id}
            className="flex flex-col border-b-2 md:flex-row justify-between items-center md:items-start gap-4 p-4 md:p-6"
          >
            {/* Title and Release Date on the left */}
            <div className="md:w-1/2">
              <Tooltip content={item.title}>
                <h2 className="text-xl font-bold mb-2 line-clamp-2 cursor-pointer">
                  {truncateString(item.title, 70)}
                </h2>
              </Tooltip>
              <p className="text-sm text-primary mb-4 font-sans font-thin">
                Release Date:{" "}
                {item.release_date
                  ? new Date(item.release_date).toLocaleDateString()
                  : new Date(item.created_at).toLocaleDateString()}
              </p>

              <div
                dangerouslySetInnerHTML={createMarkup(
                  truncateString(item.excerpt, 150)
                )}
                className="cursor-pointer text-sm font-sans font-normal"
              />
            </div>

            {/* Card on the right */}
            <div className="md:w-1/2">
              <Card shadow="none" className="border max-w-md w-full">
                <CardHeader className="flex flex-col">
                  <p className="text-sm uppercase">Analysts&apos; Comment</p>
                </CardHeader>
                <Divider />
                <CardBody>
                  <div
                    className="text-sm font-sans font-normal"
                    dangerouslySetInnerHTML={createMarkup(
                      truncateString(item.comment, 150)
                    )}
                  />
                  <Button
                    className="mt-4 bg-1B51CA text-white"
                    size="sm"
                    onPress={() => openModal(item.comment)}
                  >
                    Read More
                  </Button>
                </CardBody>
              </Card>
            </div>
          </div>
        ))
      ) : (
        <div className="flex justify-center items-center text-center mx-auto w-full min-h-[20rem] -mt-8">
          <span className="text-xl text-gray-500">No Analyst Notes Found</span>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && !isLoading && (
        <div className="flex justify-center mt-5">
          <Pagination
            key={`pagination-${totalPages}-${currentPage}`}
            loop
            showControls
            color="success"
            total={totalPages}
            page={currentPage}
            onChange={handlePageChange}
          />
        </div>
      )}

      {/* Modal */}
      <Modal
        scrollBehavior="inside"
        size="xl"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1 font-bold">
                Analysts&apos; Opinion
              </ModalHeader>
              <ModalBody>
                <div
                  dangerouslySetInnerHTML={createMarkup(selectedComment)}
                  className=" font-sans font-normal"
                />
              </ModalBody>
              <ModalFooter>
                <Button color="danger" onPress={onClose}>
                  Close
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
